import React, { Fragment, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Splitter } from "@progress/kendo-react-layout";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import {
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
  ToolbarSpacer,
  Button,
  DropDownButton,
} from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { DropDownListById } from "../../forms/form-components";
import { Form } from "../../components/form";
import { Checkbox, Input, TextArea } from "@progress/kendo-react-inputs";
import { List } from "../../components/list";
import { UsersPageForm } from "./userspageform";
import {
  canCreateUsers,
  canDeleteUsers,
  canEditUsers,
  canViewUsers,
  usersMaxReached,
} from "../../routing/authorizationhandlers";

import i18next from "i18next";
import { useTranslation } from "react-i18next";

const toolbarRenderer = (props) => {
  const { currentTheme, currentUser, pageState, t } = props;

  const saveTitle = pageState.mode == 1 ? t("users.save") : t("users.update");

  const activateTitle =
    pageState.mode == 2 && pageState.formValues.IsActive
      ? t("users.deactivate")
      : t("users.activate");
  let activateIcon =
    pageState.mode != 2
      ? ""
      : pageState.formValues.IsActive
      ? t("users.close")
      : t("users.check");

  const toolsVisibleIcon = pageState.toolsVisible ? "unpin" : "pin";
  const toolsVisibleTitle = pageState.toolsVisible
    ? t("users.hide")
    : t("users.show");

  return (
    <Toolbar>
      <ToolbarItem>
        <Button
          disabled={pageState.processing}
          style={{ color: currentTheme.primary }}
          icon={toolsVisibleIcon}
          onClick={props.onToolsToggle}
        >
          {toolsVisibleTitle}
        </Button>
      </ToolbarItem>
      <ToolbarSeparator />
      {currentUser.IsAuthenticated && canCreateUsers(currentUser) && (
        <ToolbarItem>
          <Button
            disabled={pageState.processing || usersMaxReached(currentUser)}
            style={{ color: currentTheme.primary }}
            icon="plus"
            onClick={props.onNew}
          >
            {t("users.create")}
          </Button>
        </ToolbarItem>
      )}
      {!pageState.formValues.IsSystem &&
        ((pageState.mode == 1 &&
          currentUser.IsAuthenticated &&
          canCreateUsers(currentUser)) ||
          (pageState.mode == 2 &&
            currentUser.IsAuthenticated &&
            canEditUsers(currentUser))) && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={
                  pageState.processing ||
                  pageState.formValues.IsFederationRoot ||
                  (pageState.mode == 1 && usersMaxReached(currentUser))
                }
                style={{ color: currentTheme.primary }}
                icon="save"
                onClick={props.onSave}
              >
                {saveTitle}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
      {pageState.mode == 2 && currentUser.IsAuthenticated && canEditUsers(currentUser) && (
        <Fragment>
          <ToolbarSeparator />
          <ToolbarItem>
            <Button
              disabled={
                pageState.processing ||
                pageState.formValues.IsFederationRoot ||
                pageState.formValues.IsRootSubscription
              }
              style={{ color: currentTheme.primary }}
              icon={activateIcon}
              onClick={props.onActivateDeactivate}
            >
              {activateTitle}
            </Button>
          </ToolbarItem>
        </Fragment>
      )}
      {pageState.mode == 2 &&
        currentUser.IsAuthenticated &&
        canDeleteUsers(currentUser) &&
        !pageState.formValues.IsSystem && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={
                  pageState.processing ||
                  pageState.formValues.IsFederationRoot ||
                  pageState.formValues.IsRootSubscription
                }
                style={{ color: currentTheme.primary }}
                icon="delete"
                onClick={(e) => props.onDelete(null)}
              >
                {t("users.delete")}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
    </Toolbar>
  );
};

const getEntityOperations = (entity, currentUser, t) => {
  let result = [];

  if (currentUser.IsAuthenticated && (canViewUsers(currentUser) || canEditUsers(currentUser)))
    result.push({ id: 2, text: t("users.view"), icon: "eye" });

  if (currentUser.IsAuthenticated && canDeleteUsers(currentUser))
    result.push({ id: 3, text: t("users.delete1"), icon: "delete" });

  return result;
};

const entityMenuItemRenderer =
  (currentTheme, entity, onItemClicked) => (props) => {
    let isDisabled = false;

    if (
      props.item.id == 3 &&
      (entity.IsFederationRoot || entity?.IsRootSubscription)
    )
      isDisabled = true;

    if (props.item.text)
      return (
        <div
          style={{ display: "flex", width: "100%", padding: "0px 6px" }}
          onClick={(e) => onItemClicked(entity.Id, props.item)}
        >
          <span
            className={`k-icon k-i-${props.item.icon}`}
            style={{ display: "inline-block" }}
          ></span>
          <div style={{ marginLeft: 12 }}>{props.item.text}</div>
        </div>
      );
    else return <div className="menu-separator" style={{}} />;
  };

const entitiesItemRenderer =
  (currentTheme, currentUser, t) => (props, item, index) => {
    let icon = "k-i-user";
    if (item.IsFederationRoot) icon = "k-i-gear";
    else if (item?.IsRootSubscription) icon = "k-i-unlock";

    return (
      <div
        key={item.Id}
        className={`list-item`}
        style={{
          color: item.IsActive
            ? currentTheme.bodyText
            : currentTheme.disabledText,
        }}
        onDoubleClick={(e) => {
          if (currentUser.IsAuthenticated && (canViewUsers(currentUser) || canEditUsers(currentUser)))
            props.onEntityDoubleClick(item.Id);
        }}
      >
        <div className={`list-item-icon k-icon ${icon} `}></div>
        <div className="list-item-title">{item.Name}</div>
        {currentUser.IsAuthenticated &&
          (canViewUsers(currentUser) || canEditUsers(currentUser) || canDeleteUsers(currentUser)) && (
            <div className="list-item-operations">
              <DropDownButton
                className="buttons-container-button"
                items={getEntityOperations(item, currentUser, t)}
                itemRender={entityMenuItemRenderer(
                  currentTheme,
                  item,
                  (entityId, _item) => {
                    if (_item.id == 1 || _item.id == 2)
                      //view
                      props.onEntitySelected(entityId, _item);
                    else if (_item.id == 3)
                      // delete
                      props.onDelete(entityId, _item);
                  }
                )}
                icon="more-horizontal"
                fillMode={"flat"}
              />
            </div>
          )}
      </div>
    );
  };

const MobileUsers = ({ currentTheme, currentUser, pageState, t, ...props }) => {
  const [entityFilter, setEntityFilter] = useState("");
  const user = pageState.formValues;
  const groups = user.Groups?.filter((group) => group.Checked) || [];
  const filteredUsers = pageState.entities.filter((entity) => {
    if (!entityFilter.trim()) return true;

    return entity.Name.toLowerCase().includes(entityFilter.toLowerCase());
  });

  if (pageState.mode == 2 && user.Id) {
    return (
      <div className="users-mobile-view users-mobile-details">
        <Button
          fillMode="flat"
          icon="arrow-60-left"
          className="users-mobile-back"
          onClick={props.onClose}
        >
          {t("users.close")}
        </Button>
        <div className="users-mobile-details-form">
          <div className="users-mobile-field">
            <label htmlFor="mobile-user-name">{t("users.name")}</label>
            <Input id="mobile-user-name" value={user.Name || ""} disabled />
          </div>
          <div className="users-mobile-field">
            <label htmlFor="mobile-user-email">{t("users.email")}</label>
            <Input id="mobile-user-email" value={user.Email || ""} disabled />
          </div>
          <div className="users-mobile-field">
            <label htmlFor="mobile-user-description">{t("users.description")}</label>
            <TextArea
              id="mobile-user-description"
              value={user.Description || ""}
              disabled
              rows={2}
            />
          </div>
          <div className="users-mobile-field">
            <label htmlFor="mobile-user-type">{t("users.type")}</label>
            <DropDownListById
              id="mobile-user-type"
              data={pageState.lookups.types}
              dataItemKey="Id"
              textField="Title"
              value={user.TypeId}
              disabled
              onChange={() => {}}
            />
          </div>
          {user.ClientId && (
            <div className="users-mobile-field">
              <label htmlFor="mobile-user-client-id">{t("users.clientid")}</label>
              <Input id="mobile-user-client-id" value={user.ClientId} disabled />
            </div>
          )}
          <div className="users-mobile-checkbox-field">
            <Checkbox id="mobile-user-active" checked={!!user.IsActive} disabled />
            <label htmlFor="mobile-user-active">{t("users.active")}</label>
          </div>
          <div className="users-mobile-field">
            <label htmlFor="mobile-user-groups">{t("users.groups")}</label>
            <TextArea
              id="mobile-user-groups"
              value={groups.map((group) => group.Name).join(", ")}
              disabled
              rows={2}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="users-mobile-view">
      <div className="users-mobile-heading">
        <h1>{t("users.users")}</h1>
      </div>
      <Input
        type="search"
        value={entityFilter}
        onChange={(event) => setEntityFilter(event.value)}
        placeholder={t("users.search")}
        className="users-mobile-search"
      />
      <List
        className="users-mobile-list"
        items={filteredUsers}
        itemRenderer={entitiesItemRenderer(currentTheme, currentUser, t)}
        onEntitySelected={props.onEntitySelected}
        onEntityDoubleClick={props.onEntitySelected}
        onDelete={props.onDelete}
      />
    </div>
  );
};

export const UsersPageBody = (props) => {
  const {
    onSplitterChange,
    onNew,
    onSave,
    onActivateDeactivate,
    onDelete,
    onErrorbarClose,
    onToolsToggle,
    onEntitySelected,
    onEntityDoubleClick,
    updateDirtyState,
    ...other
  } = props;

  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const pageState = useSelector((state) => state.usersPage);
  const appearance = useSelector((state) => state.appearance);
  const entities = pageState.entities;
  const [entityFilter, setEntityFilter] = useState("");

  const { t } = useTranslation();

  const getPanelBarItems = () => {
    const panelBarItems = [];

    panelBarItems.push(
      <PanelBarItem
        className="panel-entities"
        key="entities"
        title={t("users.users")}
        expanded={true}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Input
            type="search"
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.value)}
            style={{
              width: "calc(100% - 16px)",
              margin: "8px 8px 8px 8px",
              backgroundColor: "transparent",
            }}
            placeholder={t("users.search")}
          />
          <List
            className="entities"
            items={entities.filter((e) => {
              if (!entityFilter || entityFilter.trim() == "") return true;

              const filter = entityFilter.toLowerCase();
              return e.Name.toLowerCase().indexOf(filter) >= 0;
            })}
            itemRenderer={entitiesItemRenderer(currentTheme, currentUser, t)}
            onEntitySelected={onEntitySelected}
            onEntityDoubleClick={onEntityDoubleClick}
            onDelete={props.onDelete}
          />
        </div>
      </PanelBarItem>
    );

    return panelBarItems;
  };

  return (
    <Fragment>
      {pageState.processing && (
        <div className="page-loader">
          <Loader
            size={"large"}
            type="infinite-spinner"
            themeColor={"primary"}
          />
          {pageState.processingText && (
            <div style={{ fontSize: 13, color: currentTheme.primary }}>
              {pageState.processingText}
            </div>
          )}
        </div>
      )}
      {pageState.pageLoaded && (
        <Fragment>
          <div className="users-desktop-view">
            <Splitter
              style={{ border: "none" }}
              panes={pageState.panes}
              onChange={onSplitterChange}
            >
              <div className="users-page-manager">
                <PanelBar style={{ border: "none" }} expanded={[".0"]}>
                  {getPanelBarItems()}
                </PanelBar>
              </div>

              <Form
                className="users-page-form"
                currentTheme={currentTheme}
                currentUser={currentUser}
                toolbarRenderer={toolbarRenderer}
                t={t}
                onNew={onNew}
                onSave={onSave}
                onActivateDeactivate={onActivateDeactivate}
                onDelete={onDelete}
                errorMessages={pageState.formServerErrors}
                onErrorbarClose={onErrorbarClose}
                onToolsToggle={onToolsToggle}
                pageState={pageState}
              >
                {pageState.mode > 0 ? (
                  <UsersPageForm
                    onGroupCheck={props.onGroupCheck}
                    updateDirtyState={updateDirtyState}
                  />
                ) : (
                  <Fragment></Fragment>
                )}
              </Form>

              {currentTheme.pageHelpVisible && <div></div>}
            </Splitter>
          </div>
          <MobileUsers
            currentTheme={currentTheme}
            currentUser={currentUser}
            pageState={pageState}
            t={t}
            onEntitySelected={onEntitySelected}
            onDelete={onDelete}
            onClose={props.onMobileClose}
          />
        </Fragment>
      )}
      <style>
        {`                  
                    .users-page > .content
                    {
                      
                    }

                    .users-wrapper{ 
                        direction:${appearance.isRightToLeft ? "rtl" : "ltr"}                      
                    }    
                    .users-page .k-splitter{
                      flex-direction:${
                        appearance.isRightToLeft ? "row-reverse" : "row"
                      } 
                    }
                      
                    .users-page .k-splitter .k-pane{
                      direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 
                    }

                    .users-page > .content .users-page-manager
                    {
                      border: ${currentTheme.managerBorder};
                      border-radius: ${currentTheme.pageBorderRadius};
                      background-color: ${currentTheme.componentBgFaded};
                    }                

                    .users-page > .content .users-page-manager .k-panelbar
                    {
                      /*required*/
                      background-color: transparent;
                    }

                    .users-page > .content .users-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link .k-icon
                    {
                      /*the arrow near every panel header*/
                      color: ${currentTheme.touchColor}!important;
                      margin-right: 6px;
                    }

                    .users-page > .content .users-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link
                    {
                      /*the span in the above <li> i.e. the title of every group in the manager: datasets, users, columns and description*/  
                      background-color: transparent;
                      color: ${currentTheme.touchColor};
                      border-bottom: 1px solid ${currentTheme.separatorColor}; 
                    }

                    .users-page > .content .users-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link:hover
                    {
                      /*the above hovered - do nothing*/
                    }

                    .users-page > .content .users-page-manager .panel-entities .list-item:hover
                    {
                      /*expression item hover */
                      background-color: ${currentTheme.componentBorderColor};
                      cursor: pointer;
                    }

                    .users-page > .content .users-page-manager .panel-entities .k-input::placeholder
                    {
                        color: ${currentTheme.componentText};
                        /*font-style: italic;*/
                    }
                   
                    .users-page > .content .users-page-manager .list-container
                    {
                      /*bg of both the exp. and columns Lists*/
                      background-color: transparent;
                      padding : 0;/*can add padding to list items*/
                    }

                    .users-page > .content .users-page-manager .k-panelbar-header
                    {
                      background-color: transparent;
                    }

                    .users-page > .content .users-page-manager .k-panelbar-content
                    {
                      background-color: transparent;
                    }

                    .users-page > .content .users-page-manager .entities .list-item-icon
                    {
                      /*first icon in users list*/
                      padding-left: 12px
                    }                   

                    .users-page > .content .users-page-manager .entities .list-item-title
                    {
                      vertical-align: middle;/*fine tuning*/
                    }                    

                    /*the form*/
                    .users-page-form   
                    {
                        background-color: transparent;
                        padding-left: 0px;
                        border: ${currentTheme.pageFormBorder};   
                        border-radius: ${currentTheme.pageBorderRadius};
                    }

                    /*form toolbar*/
                    .users-page-form > .toolbar .k-toolbar
                    {
                        width: 100%;
                        border-left: none !important;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                        border-top: none !important;
                        border-right: none !important;
                        background-color: ${
                          currentTheme.toolbarBg
                        };/*transparent;*/
                    }

                    .users-page > .content .toolbar .k-button
                    {
                      /*the toolbar buttons*/
                      border: none;
                      padding: 6px 8px;
                    }

                    .users-page > .content .toolbar .k-button-text
                    {
                      /*color of the text in every toolbar button=black in azure theme*/
                      color: ${currentTheme.toolbarText};
                    }

                    .users-page > .content .toolbar .k-button-icon
                    {
                      font-weight: 500;
                      color: ${currentTheme.primary};
                    }

                    .users-page > .content .form-root .k-form .k-form-field:nth-child(1)
                    {
                        margin-top: 4px;
                    }

                    .users-page > .content .form-root .k-form .k-form-field
                    {
                        margin-top: 32px;
                    }

                    /*.users-page > .content .form-root .k-form .k-form-field:nth-child(6)                   
                    {
                        margin-top: -4px !important;
                        width: 100%;
                    }*/

                    .users-page > .content .form-root .content
                    {
                      background-color: ${currentTheme.pageContentBg};
                    }

                    /*styling the popup menu */
                    .k-popup ul{
                        padding: 0px;
                    }

                    .k-popup ul > li{
                        margin: 0;
                        padding: 0px;                                
                    }

                    .k-popup ul > li > span{
                        border: none !important;
                        box-shadow: none !important;
                    }

                    .k-menu-link{
                        padding: 0px 0px !important;
                        
                    }

                    .users-desktop-view {
                      flex: 0 0 100%;
                      width: 100%;
                      min-width: 0;
                      height: 100%;
                    }

                    .users-desktop-view .k-splitter {
                      width: 100%;
                      height: 100%;
                    }

                    .users-mobile-view {
                      display: none;
                    }

                    @media (max-width: 1024px) {
                      .users-page > .content {
                        overflow-y: auto;
                      }

                      .users-desktop-view {
                        display: none;
                      }

                      .users-mobile-view {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                        min-height: 100%;
                        padding: 18px 16px;
                        box-sizing: border-box;
                        background: ${currentTheme.componentBg};
                        color: ${currentTheme.componentText};
                        direction: ${appearance.isRightToLeft ? "rtl" : "ltr"};
                      }

                      .users-mobile-heading h1 {
                        margin: 0 0 18px;
                        color: ${currentTheme.componentText};
                        font-size: 21px;
                        font-weight: 600;
                      }

                      .users-page .users-mobile-search {
                        margin-bottom: 12px;
                        background: transparent;
                      }

                      .users-mobile-list,
                      .users-mobile-list .list-root {
                        width: 100%;
                        box-sizing: border-box;
                      }

                      .users-mobile-list .list-root {
                        display: block;
                      }

                      .users-mobile-list .list-root > div {
                        display: block;
                      }

                      .users-mobile-list .list-root > div > div {
                        display: block;
                        padding: 0;
                      }

                      .users-page .users-mobile-list .list-item {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        padding: 12px 0;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                        color: ${currentTheme.componentText};
                      }

                      .users-page .users-mobile-list .list-item-icon {
                        width: 24px;
                        color: ${currentTheme.primary};
                        font-size: 17px;
                      }

                      .users-page .users-mobile-list .list-item-title {
                        flex: 1;
                        min-width: 0;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        font-size: 14px;
                        font-weight: 600;
                      }

                      .users-page .users-mobile-list .list-item-operations {
                        flex: 0 0 auto;
                      }

                      .users-page .users-mobile-list .buttons-container-button {
                        min-width: auto;
                        padding: 4px;
                        color: ${currentTheme.primary};
                      }

                      .users-page .users-mobile-details {
                        padding-top: 10px;
                      }

                      .users-page .users-mobile-back {
                        align-self: flex-start;
                        margin-bottom: 18px;
                        color: ${currentTheme.primary};
                      }

                      .users-mobile-details-form {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                        gap: 16px;
                        box-sizing: border-box;
                      }

                      .users-mobile-field {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                      }

                      .users-mobile-field > label,
                      .users-mobile-checkbox-field > label {
                        font-size: 12px;
                        color: ${currentTheme.inputLabelColor};
                      }

                      .users-mobile-details-form input.k-input,
                      .users-mobile-details-form textarea.k-textarea,
                      .users-mobile-details-form .k-dropdownlist .k-input-inner {
                        padding: 6px 8px;
                      }

                      .users-mobile-checkbox-field {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                      }
                    }
                  `}
      </style>
    </Fragment>
  );
};
